/**
 * PLT-7780: the console's beacon for Eevo's cookieless conversion funnel (`src/common/arvo/funnel.ts`).
 * One `console_view` per page load of an Eevo host, sent with the session cookie, so the server
 * tags it with the signed-in person and can link their sign-up to the landing visit
 * (`upserve/modules/arvo/funnel/attribution.ts`). A sign-in returns with a full page load, so the
 * person's first signed-in view sends one of its own.
 *
 * It carries the landing page's visit id (`ev`) and A/B variants (`evv`) from the address, and
 * whether LinkedIn's click id (`li_fat_id`) was there, then removes all three from it, so a reload
 * or a shared link does not carry them on. On the billing page it also sends the promo code a promo
 * link filled in, and leaves it in the address for the billing page to use. Nothing is stored in
 * the browser, so no consent is asked and it runs for every visitor. It is the only analytics on
 * Eevo's hosts (PLT-7905).
 */
import {
  EEVO_FUNNEL_VARIANTS_PARAM,
  EEVO_FUNNEL_VISIT_PARAM,
  EEVO_INVITE_SOURCE,
  EEVO_UTM_KEYS,
  LINKEDIN_CLICK_ID_PARAM,
  type EevoFunnelHitPayload,
  type EevoUtmKey,
  parseEevoVariantsParam,
} from '@upright/common/arvo/funnel';
import { PROMO_CODE_QUERY_PARAM } from '@upright/common/arvo/promo-codes';
import { INVITE_QUERY_PARAM } from '@upright/common/arvo/referrals';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const HIT_PATH = '/api/arvo/funnel/hit';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

let sent = false;

function sendHit(payload: EevoFunnelHitPayload): void {
  const body = JSON.stringify(payload);
  try {
    // `text/plain` is what a beacon may send without a preflight; the server parses it as JSON.
    if (navigator.sendBeacon(HIT_PATH, new Blob([body], { type: 'text/plain' }))) return;
  } catch {
    // Falls through to fetch.
  }
  void fetch(HIT_PATH, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'text/plain' } }).catch(
    () => undefined,
  );
}

/** Mount once, around all of an Eevo host's routes. */
export function EevoFunnelBeacon() {
  const navigate = useNavigate();

  useEffect(() => {
    if (sent) return;
    sent = true;
    const { pathname, search, hash } = window.location;
    const params = new URLSearchParams(search);
    const visitId = params.get(EEVO_FUNNEL_VISIT_PARAM) ?? '';
    const utm: Partial<Record<EevoUtmKey, string>> = Object.fromEntries(
      EEVO_UTM_KEYS.flatMap(key => {
        const value = params.get(key);
        return value ? [[key, value.slice(0, 200)]] : [];
      }),
    );
    // An invite link carries no UTM of its own, to stay short to share; its code says where the
    // visitor came from (PLT-7791).
    if (params.has(INVITE_QUERY_PARAM) && !utm.utm_source) utm.utm_source = EEVO_INVITE_SOURCE;
    sendHit({
      event: 'console_view',
      visitId: UUID.test(visitId) ? visitId : undefined,
      path: pathname,
      referrer: document.referrer || undefined,
      utm,
      variants: parseEevoVariantsParam(params.get(EEVO_FUNNEL_VARIANTS_PARAM)),
      // An ad that links straight to Eevo (PLT-7831). Only whether the click id was there is sent.
      linkedinClick: Boolean(params.get(LINKEDIN_CLICK_ID_PARAM)) || undefined,
      // A promo link (PLT-7870). Read on the billing page only: elsewhere `code` can be a sign-in
      // provider's authorization code, which must never leave the page.
      promoCode: (pathname.endsWith('/billing') && params.get(PROMO_CODE_QUERY_PARAM)?.slice(0, 40)) || undefined,
    });
    const carried = [EEVO_FUNNEL_VISIT_PARAM, EEVO_FUNNEL_VARIANTS_PARAM, LINKEDIN_CLICK_ID_PARAM];
    if (carried.some(param => params.has(param))) {
      for (const param of carried) params.delete(param);
      const rest = params.toString();
      navigate({ pathname, search: rest ? `?${rest}` : '', hash }, { replace: true });
    }
  }, [navigate]);

  return null;
}
